import { Form, message } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

export const usePromotion = () => {
  const [promotions, setPromotions] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [promotionType, setPromotionType] = useState(null); // Track promotion type
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPromotions();
    fetchRooms();
    fetchRatePlans();
  }, []);

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/promotions/getallpromotions");
      setPromotions(Array.isArray(response.data) ? response.data : []); // Ensure it's an array
    } catch (error) {
      message.error("Failed to fetch promotions");
      setPromotions([]); // Set an empty array in case of error
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axios.get("/api/rooms/getallrooms");
      setRooms(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error("Failed to fetch rooms");
    }
  };

  const fetchRatePlans = async () => {
    try {
      const response = await axios.get("/api/rateplans/getallrateplans");
      setRatePlans(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error("Failed to fetch rate plans");
    }
  };

  const handleCreate = () => {
    setEditingPromotion(null);
    setPromotionType(null); // Reset promotion type
    setIsModalVisible(true);
  };

  const handleEdit = (promotion) => {
    setEditingPromotion(promotion);
    setPromotionType(promotion.promotionType);
    setIsModalVisible(true);

    // Prepare fields to patch into the form
    const patch = {
      ...promotion,
      applicableStayDates: [
        dayjs(promotion.applicableStayDates.startDate),
        dayjs(promotion.applicableStayDates.endDate),
      ],
      applicableBookDates: [
        dayjs(promotion.applicableBookDates.startDate),
        dayjs(promotion.applicableBookDates.endDate),
      ],
    };

    // Add back condition fields
    if (promotion.promotionType === "Last Minute") {
      patch.lastMinuteDays = promotion.condition?.lastMinuteDays;
    } else if (promotion.promotionType === "Early Bird") {
      patch.earlyBirdDays = promotion.condition?.earlyBirdDays;
    }

    form.setFieldsValue(patch);
  };

  const handleDelete = async (promotionId) => {
    try {
      await axios.delete(`/api/promotions/delete/${promotionId}`);
      message.success("Promotion deleted successfully");
      fetchPromotions();
    } catch (error) {
      message.error("Failed to delete promotion");
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const { applicableStayDates, applicableBookDates, promoCode, ...rest } =
        values;

      const payload = {
        ...rest,
        promoCode: promotionType === "Promo Code" ? promoCode : undefined, // Only send if Promo Code type
        applicableStayDates: {
          startDate: applicableStayDates[0].toISOString(),
          endDate: applicableStayDates[1].toISOString(),
        },
        applicableBookDates: {
          startDate: applicableBookDates[0].toISOString(),
          endDate: applicableBookDates[1].toISOString(),
        },
      };

      if (editingPromotion) {
        await axios.put(
          `/api/promotions/update/${editingPromotion._id}`,
          payload
        );
        message.success("Promotion updated successfully");
      } else {
        await axios.post("/api/promotions/create", payload);
        message.success("Promotion created successfully");
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchPromotions();
    } catch (error) {
      message.error("Failed to save promotion");
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handlePromotionTypeChange = (value) => {
    setPromotionType(value);
  };

  return {
    editingPromotion,
    form,
    promotionType,
    promotions,
    rooms,
    ratePlans,
    loading,
    isModalVisible,
    handleCreate,
    handleEdit,
    handleDelete,
    handleModalOk,
    handleModalCancel,
    handlePromotionTypeChange,
  };
};
